
import { Event, EventFilters, EventType } from './types';
import { parseISO, isAfter, isBefore, isEqual } from 'date-fns';

// Default filters to use when no filters are provided
export const DEFAULT_FILTERS: EventFilters = {
  type: [],
  audience: [],
  professions: [],
  location: '',
  search: '',
  hasCreditPoints: false,
  dateRange: {
    startDate: '',
    endDate: ''
  }
};

// Helper functions for filtering events
export const filterEvents = (events: Event[], filters: EventFilters) => {
  let filtered = [...events];

  if (filters.type && filters.type.length > 0) {
    filtered = filtered.filter(event => filters.type?.includes(event.type));
  }

  // Filter by professions instead of audience
  if (filters.professions && filters.professions.length > 0) {
    filtered = filtered.filter(event =>
      event.professions && filters.professions?.some(id => event.professions?.includes(id))
    );
  }

  if (filters.location) {
    filtered = filtered.filter(event =>
      event.venue.city.toLowerCase().includes(filters.location?.toLowerCase() || '') ||
      event.venue.region?.toLowerCase().includes(filters.location?.toLowerCase() || '')
    );
  }

  if (filters.dateRange) {
    const { startDate, endDate } = filters.dateRange;

    if (startDate && endDate) {
      // Parse the dates
      const filterStartDate = parseISO(startDate);
      const filterEndDate = parseISO(endDate);

      // Filter events that fall within the date range
      filtered = filtered.filter(event => {
        const eventStart = parseISO(event.dateStart);
        const eventEnd = parseISO(event.dateEnd);

        // An event is within the date range if:
        // 1. Event starts on or after the filter start date AND before or on the filter end date
        // OR
        // 2. Event ends on or after the filter start date AND before or on the filter end date
        // OR
        // 3. Event starts before the filter start date AND ends after the filter end date (spans the entire range)

        return (
          // Case 1: Event starts within the range
          (isAfter(eventStart, filterStartDate) || isEqual(eventStart, filterStartDate)) &&
          (isBefore(eventStart, filterEndDate) || isEqual(eventStart, filterEndDate))
        ) || (
            // Case 2: Event ends within the range
            (isAfter(eventEnd, filterStartDate) || isEqual(eventEnd, filterStartDate)) &&
            (isBefore(eventEnd, filterEndDate) || isEqual(eventEnd, filterEndDate))
          ) || (
            // Case 3: Event spans the entire range
            isBefore(eventStart, filterStartDate) && isAfter(eventEnd, filterEndDate)
          );
      });
    } else if (startDate) {
      // Only filter by start date
      const filterStartDate = parseISO(startDate);
      filtered = filtered.filter(event => {
        const eventStart = parseISO(event.dateStart);
        return isAfter(eventStart, filterStartDate) || isEqual(eventStart, filterStartDate);
      });
    } else if (endDate) {
      // Only filter by end date
      const filterEndDate = parseISO(endDate);
      filtered = filtered.filter(event => {
        const eventStart = parseISO(event.dateStart);
        return isBefore(eventStart, filterEndDate) || isEqual(eventStart, filterEndDate);
      });
    }
  }

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(event =>
      event.name.toLowerCase().includes(searchLower) ||
      event.description.toLowerCase().includes(searchLower) ||
      (event.organizer && event.organizer.name.toLowerCase().includes(searchLower)) ||
      (event.tags && event.tags.some(tag => tag.toLowerCase().includes(searchLower)))
    );
  }

  if (filters.hasCreditPoints) {
    // Modified to include events with creditPoints > 0 OR postAccredited = true
    filtered = filtered.filter(event =>
      (event.creditPoints !== undefined && event.creditPoints > 0) ||
      event.postAccredited === true
    );
  }

  return filtered;
};

// Group events by date for calendar view
export const groupEventsByDate = (events: Event[]) => {
  const groupedEvents: Record<string, Event[]> = {};

  events.forEach(event => {
    const date = new Date(event.dateStart).toISOString().split('T')[0];
    if (!groupedEvents[date]) {
      groupedEvents[date] = [];
    }
    groupedEvents[date].push(event);
  });

  return groupedEvents;
};

// Get featured events
export const getFeaturedEvents = (events: Event[]) => {
  return events.filter(event => event.isFeatured);
};

// Get upcoming events
export const getUpcomingEvents = (events: Event[], limit?: number) => {
  const now = new Date();
  const upcoming = events
    .filter(event => new Date(event.dateStart) > now)
    .sort((a, b) => new Date(a.dateStart).getTime() - new Date(b.dateStart).getTime());

  return limit ? upcoming.slice(0, limit) : upcoming;
};

// Get events by type
export const getEventsByType = (events: Event[], type: EventType) => {
  return events.filter(event => event.type === type);
};
